/**
 * Investment & Return Projection Engine (Phase-1).
 *
 * Consumes POST /api/franchise-projection/calculate and renders the
 * founder-friendly KPI tiles, Investment Recovery tracker, Required
 * Monthly Performance cards, 60-month projection table and Year 1-5
 * summary cards.
 *
 * Model-aware — automatically switches labels for Profit Share vs
 * Revenue Share franchises. Seasonality / scenarios / downloads are
 * Phase-2 concerns and intentionally left out of this file.
 */
import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { Alert, AlertDescription } from "./ui/alert";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Loader2, RefreshCw, TrendingUp, TrendingDown, Wallet, Target,
  Calendar, AlertCircle, CheckCircle2, Info, DollarSign,
  FileDown, FileSpreadsheet,
} from "lucide-react";
import { toast } from "sonner";
import { OwnerKpi, MetalChip, SummaryStrip } from "./owner-reports/OwnerUI";
import { Landmark, Percent, Flag, Coins, Hourglass, CalendarCheck, Gauge, LayoutGrid, ChevronDown, ChevronUp } from "lucide-react";

const STATUS_TONE = { ahead: "green", on_track: "green", slightly_behind: "amber", materially_behind: "red" };
const TONE_FILL = { green: "emerald", amber: "gold", red: "ruby", blue: "blue", purple: "purple", neutral: "teal" };

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => {
  try { return (JSON.parse(localStorage.getItem("pb_session_v2") || "{}").token) || ""; }
  catch { return ""; }
};

// ── Formatters ──────────────────────────────────────────────────────────────
const currencyLocale = (cur) => {
  const c = (cur || "INR").toUpperCase();
  if (c === "AUD") return "en-AU";
  if (c === "USD") return "en-US";
  if (c === "GBP") return "en-GB";
  return "en-IN";
};

const fmtMoney = (n, cur) => {
  const c = (cur || "INR").toUpperCase();
  try {
    return new Intl.NumberFormat(currencyLocale(c), {
      style: "currency", currency: c, maximumFractionDigits: 0,
    }).format(Number(n || 0));
  } catch {
    return `${c} ${Number(n || 0).toLocaleString()}`;
  }
};
const fmtMoney2 = (n, cur) => {
  const c = (cur || "INR").toUpperCase();
  try {
    return new Intl.NumberFormat(currencyLocale(c), {
      style: "currency", currency: c, minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(Number(n || 0));
  } catch {
    return `${c} ${Number(n || 0).toLocaleString()}`;
  }
};
const fmtPct = (n) => `${Number(n || 0).toFixed(2)}%`;

// ── Status badge ────────────────────────────────────────────────────────────
const STATUS_META = {
  ahead:              { label: "Ahead of Plan",       cls: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: <TrendingUp className="w-3 h-3 mr-1" /> },
  on_track:           { label: "On Track",             cls: "bg-blue-100 text-blue-800 border-blue-300",         icon: <CheckCircle2 className="w-3 h-3 mr-1" /> },
  slightly_behind:    { label: "Slightly Behind",      cls: "bg-amber-100 text-amber-800 border-amber-300",      icon: <AlertCircle className="w-3 h-3 mr-1" /> },
  materially_behind:  { label: "Materially Behind",    cls: "bg-rose-100 text-rose-800 border-rose-300",         icon: <TrendingDown className="w-3 h-3 mr-1" /> },
};
function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.on_track;
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full border text-xs font-semibold ${m.cls}`} data-testid={`recovery-status-${status}`}>
      {m.icon}{m.label}
    </span>
  );
}

// ── KPI Tile ────────────────────────────────────────────────────────────────
function KpiTile({ label, value, sublabel, icon, testId, tone = "blue" }) {
  const toneMap = {
    blue:    "from-blue-50 to-blue-100 border-blue-200 text-blue-900",
    emerald: "from-emerald-50 to-emerald-100 border-emerald-200 text-emerald-900",
    amber:   "from-amber-50 to-amber-100 border-amber-200 text-amber-900",
    rose:    "from-rose-50 to-rose-100 border-rose-200 text-rose-900",
    violet:  "from-violet-50 to-violet-100 border-violet-200 text-violet-900",
    slate:   "from-slate-50 to-slate-100 border-slate-200 text-slate-900",
  };
  return (
    <div className={`rounded-lg border bg-gradient-to-br ${toneMap[tone]} p-4`} data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="text-[11px] uppercase tracking-wide font-semibold opacity-80">{label}</div>
        {icon && <div className="opacity-70">{icon}</div>}
      </div>
      <div className="mt-2 text-2xl font-bold">{value}</div>
      {sublabel && <div className="mt-1 text-[11px] opacity-70">{sublabel}</div>}
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────
export function InvestmentReturnProjection({ center, franchiseCode, readOnly = false, strip = false }) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [periodType, setPeriodType] = useState("5y");
  const [targetPct, setTargetPct] = useState(22);
  const [downloading, setDownloading] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // Resolve the identifier passed to the backend. Prefer explicit franchiseCode,
  // fall back to `center` (works for both Center Accounts and Owner Reports).
  const resolvedCode = (franchiseCode || center || "").toUpperCase();

  // Trigger a manual refresh (button click).
  const refresh = () => setRefreshTick(t => t + 1);

  // Fetch on center / period / target / refresh changes.
  useEffect(() => {
    if (!resolvedCode) return;
    let cancelled = false;
    setLoading(true); setError(null);
    (async () => {
      try {
        const res = await fetch(`${API}/api/franchise-projection/calculate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            token: getToken(),
            franchise_code: resolvedCode,
            center: resolvedCode,
            projection_period_type: periodType,
            target_return_pct: targetPct,
          }),
        });
        const json = await res.json();
        if (cancelled) return;
        if (!res.ok || json?.success === false) {
          throw new Error(json?.detail || json?.message || "Failed to load projection");
        }
        setData(json);
      } catch (e) {
        if (cancelled) return;
        setError(e.message || "Unable to load projection");
        setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [resolvedCode, periodType, targetPct, refreshTick]);

  useEffect(() => { /* fetch runs in the main effect above */ }, []);

  const download = async (kind) => {
    if (!resolvedCode) return;
    setDownloading(kind);
    try {
      const res = await fetch(`${API}/api/franchise-projection/export-${kind}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: getToken(),
          franchise_code: resolvedCode,
          center: resolvedCode,
          projection_period_type: periodType,
          target_return_pct: targetPct,
        }),
      });
      if (!res.ok) {
        const errJson = await res.json().catch(() => ({}));
        throw new Error(errJson.detail || `Download failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers.get("content-disposition") || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      a.download = match ? match[1] : `Investment_Return_Projection.${kind === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${kind.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(e.message || `Failed to download ${kind}`);
    } finally {
      setDownloading(null);
    }
  };

  const cur = data?.investment?.currency || "INR";
  const inv = data?.investment || {};
  const rt = data?.return_target || {};
  const rec = data?.recovery || {};
  const req = data?.required || {};
  const terms = data?.commercial_terms || {};
  const cfg = data?.config || {};
  const modelLabel = terms.payout_model === "profit_share" ? "Profit Share" : "Revenue Share";

  const monthlyRows = data?.monthly || [];
  const years = data?.years || [];

  const paginatedMonths = useMemo(() => monthlyRows, [monthlyRows]);

  const stTone = STATUS_TONE[rec.status] || "blue";
  const metrics = data ? [
    { k: "investment", label: "Total Investment", value: fmtMoney(inv.actual_investment_amount, cur), sub: inv.investment_date ? `Confirmed ${inv.investment_date}` : "Actual capital deployed", tone: "blue", Icon: Landmark },
    { k: "target-return", label: "Target Return", value: fmtPct(rt.target_return_pct), sub: `(${fmtMoney(rt.target_return_amount, cur)})`, tone: "purple", Icon: Percent },
    { k: "target-recovery", label: "Target Recovery", value: fmtMoney(rt.target_recovery, cur), sub: "Investment + Return", tone: "neutral", Icon: Flag },
    { k: "recovered", label: "Recovered to Date", value: fmtMoney(rec.cumulative_owner_earned, cur), sub: `Recovery ${fmtPct(rec.recovery_pct)}`, tone: "green", Icon: Coins },
    { k: "remaining", label: "Remaining", value: fmtMoney(rec.remaining_to_target, cur), sub: `${req.remaining_months || 0} months left`, tone: "amber", Icon: Hourglass },
    { k: "expected-date", label: "Expected Recovery", value: rec.expected_recovery_ym || "—", sub: (STATUS_META[rec.status] || STATUS_META.on_track).label, tone: stTone, Icon: CalendarCheck },
    { k: "progress", label: "Recovery Progress", value: fmtPct(rec.recovery_pct), sub: `Ideal by now ${fmtPct(rec.ideal_pct_by_now)}`, tone: stTone, Icon: Gauge },
  ] : [];
  const showDetails = strip || detailsOpen;

  if (!resolvedCode) {
    return (
      <Card><CardContent className="py-8 text-center text-muted-foreground">
        Select a centre to view the Investment &amp; Return Projection.
      </CardContent></Card>
    );
  }

  const body = (
    <div className="space-y-4" data-testid="investment-return-projection">
      {/* Header + Controls */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5 text-emerald-600" />
                Franchise Investment &amp; Return Projection
                {readOnly && (
                  <Badge variant="outline" className="ml-2 text-[10px] bg-slate-100">Read-only View</Badge>
                )}
                {data?.phase && (
                  <Badge variant="outline" className="ml-2 text-[10px]">Phase {data.phase} · Preview</Badge>
                )}
              </CardTitle>
              <CardDescription>
                {readOnly
                  ? "Read-only owner view. Numbers, model and assumptions are managed from Center Accounts."
                  : "Investment-driven, model-aware projection engine. Historical months are frozen actuals; future months recalculate from the remaining recovery target."}
              </CardDescription>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {!readOnly && (
                <>
                  <div className="flex items-center gap-1">
                    <Label className="text-xs">Target %</Label>
                    <Input
                      type="number"
                      className="w-20 h-8"
                      value={targetPct}
                      onChange={(e) => setTargetPct(parseFloat(e.target.value) || 0)}
                      data-testid="target-return-input"
                    />
                  </div>
                  <Select value={periodType} onValueChange={setPeriodType} data-testid="period-select">
                    <SelectTrigger className="w-32 h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5y">5 Years</SelectItem>
                      <SelectItem value="7y">7 Years</SelectItem>
                      <SelectItem value="agreement">Agreement Term</SelectItem>
                    </SelectContent>
                  </Select>
                </>
              )}
              <Button size="sm" onClick={refresh} disabled={loading}
                      data-testid="refresh-projection-btn" variant="outline">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                <span className="ml-1">Refresh</span>
              </Button>
              <Button size="sm" onClick={() => download("pdf")} disabled={downloading === "pdf" || !data}
                      data-testid="projection-download-pdf-btn" className="bg-rose-600 hover:bg-rose-700 text-white">
                {downloading === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                <span className="ml-1">PDF</span>
              </Button>
              <Button size="sm" onClick={() => download("excel")} disabled={downloading === "excel" || !data}
                      data-testid="download-excel-btn" className="bg-emerald-700 hover:bg-emerald-800 text-white">
                {downloading === "excel" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
                <span className="ml-1">Excel</span>
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading && !data && (
            <div className="py-8 text-center text-sm text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading projection…
            </div>
          )}

          {error && (
            <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>
          )}

          {data && (
            <>
              {/* Franchise summary strip */}
              <div className="flex flex-wrap gap-4 text-xs mb-4 p-3 bg-slate-50 rounded border border-slate-200">
                <div><span className="text-muted-foreground">Centre:</span> <strong>{data.franchise?.franchise_code}</strong></div>
                <div><span className="text-muted-foreground">Country:</span> <strong>{data.franchise?.country}</strong></div>
                <div><span className="text-muted-foreground">City:</span> <strong>{data.franchise?.city || "—"}</strong></div>
                <div><span className="text-muted-foreground">Model:</span> <Badge variant="outline">{modelLabel}</Badge></div>
                <div><span className="text-muted-foreground">Owner %:</span> <strong>{terms.owner_pct}%</strong></div>
                <div><span className="text-muted-foreground">MFPL %:</span> <strong>{terms.company_pct}%</strong></div>
                <div><span className="text-muted-foreground">Horizon:</span> <strong>{cfg.projection_period_months} months</strong></div>
                <div><span className="text-muted-foreground">Start:</span> <strong>{cfg.start_ym}</strong></div>
                <div><span className="text-muted-foreground">End:</span> <strong>{cfg.end_ym}</strong></div>
              </div>

              {/* Investment integrity warning */}
              {(inv.actual_investment_amount || 0) <= 0 && (
                <Alert className="mb-4 border-amber-300 bg-amber-50">
                  <Info className="w-4 h-4 text-amber-700" />
                  <AlertDescription className="text-amber-800">
                    <strong>Actual Investment Amount is not set for this franchise.</strong> Please open
                    <em> Franchise Management → {data.franchise?.franchise_code} → Actual Investment</em> and
                    record the confirmed capital deployed. Projection numbers below assume zero investment
                    and will therefore show zero recovery target.
                  </AlertDescription>
                </Alert>
              )}

              {/* KPI TILES */}
              {!strip && (
                <div className="grid grid-cols-2 md:grid-cols-4 min-[1600px]:grid-cols-7 gap-3 mb-4" data-testid="investment-metal-blocks">
                  {metrics.map((m, i) => <OwnerKpi key={m.k} i={i} fill={TONE_FILL[m.tone]} Icon={m.Icon} label={m.label} value={m.value} sub={m.sub} testid={`kpi-${m.k}`} />)}
                </div>
              )}

              {/* Progress bar */}
              <div className="mb-6">
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="font-semibold">Investment Recovery Progress</span>
                  <span className="text-muted-foreground">
                    <strong>{fmtPct(rec.recovery_pct)}</strong> of <strong>{fmtPct(100)}</strong>
                    &nbsp;·&nbsp;ideal-by-now <strong>{fmtPct(rec.ideal_pct_by_now)}</strong>
                  </span>
                </div>
                <Progress value={Math.min(100, rec.recovery_pct || 0)} className="h-3" data-testid="recovery-progress" />
              </div>

              {!strip && (
                <button type="button" onClick={() => setDetailsOpen(!detailsOpen)} className="mb-4 flex w-full items-center gap-2 rounded-xl border-2 border-[#e2c27a] bg-[#fffdf8] px-4 py-3 text-left text-lg font-bold text-[#750817]" data-testid="investment-details-toggle">
                  <LayoutGrid className="w-5 h-5" /><span className="flex-1">Investment Projection Details (Grid)</span>
                  <span className="text-sm font-semibold text-[#5a3a22]">{detailsOpen ? "Hide Details" : "View Details"}</span>{detailsOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
              )}
              {showDetails && <>
              {/* Required Monthly Performance */}
              <Card className="mb-6 border-emerald-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Target className="w-4 h-4 text-emerald-600" /> Required Monthly Performance (average)
                  </CardTitle>
                  <CardDescription>
                    Backwards math from the <strong>{fmtMoney(rec.remaining_to_target, cur)}</strong> still to
                    recover over the next <strong>{req.remaining_months || 0}</strong> months.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <KpiTile
                      label="Required Owner Distribution / month"
                      value={fmtMoney2(req.required_owner_per_month, cur)}
                      sublabel={`= ${fmtMoney(rec.remaining_to_target, cur)} ÷ ${req.remaining_months || 0}`}
                      tone="emerald"
                      testId="kpi-required-owner"
                    />
                    <KpiTile
                      label={terms.payout_model === "profit_share"
                        ? "Required Distributable Profit / month"
                        : "Required Revenue-Share Base / month"}
                      value={fmtMoney2(req.required_share_base_per_month, cur)}
                      sublabel={`= Owner ÷ ${terms.owner_pct}%`}
                      tone="violet"
                      testId="kpi-required-base"
                    />
                    <KpiTile
                      label="Total Remaining Target"
                      value={fmtMoney(req.remaining_target_recovery, cur)}
                      sublabel="To fully recover investment + return"
                      tone="amber"
                      testId="kpi-remaining-target"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Historical actuals summary */}
              {data.historical?.rows?.length > 0 && (
                <Card className="mb-6">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Historical Performance (Actuals)</CardTitle>
                    <CardDescription>
                      {data.historical.rows.length} completed month(s) from {data.historical.range.from} →&nbsp;
                      {data.historical.range.to}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {["last_3m", "last_6m", "last_12m", "all_time"].map((w) => {
                        const s = data.historical.summary.averages[w] || {};
                        const label = w === "all_time" ? "All Time" : `Last ${w.replace("last_", "").replace("m", "M")}`;
                        return (
                          <div key={w} className="p-3 rounded border bg-slate-50">
                            <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-600 mb-1">
                              {label} · {s.n || 0} mo
                            </div>
                            <div className="text-xs space-y-0.5">
                              <div className="flex justify-between"><span className="text-muted-foreground">Avg Sales</span><strong>{fmtMoney(s.sale, cur)}</strong></div>
                              <div className="flex justify-between"><span className="text-muted-foreground">Avg Profit</span><strong>{fmtMoney(s.profit, cur)}</strong></div>
                              <div className="flex justify-between"><span className="text-muted-foreground">Avg Owner Share</span><strong>{fmtMoney(s.owner_share, cur)}</strong></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Year Summary */}
              {years.length > 0 && (
                <Card className="mb-6">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Year-by-Year Summary</CardTitle>
                    <CardDescription>Aggregated across the {cfg.projection_period_months}-month horizon.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="year-summary">
                      {years.slice(0, 7).map((y) => (
                        <div key={y.year} className="p-3 rounded border bg-white">
                          <div className="flex items-center justify-between">
                            <div className="text-sm font-semibold">Year {y.year}</div>
                            <Badge variant="outline" className="text-[10px]">
                              {y.actual_months}A / {y.projected_months}P
                            </Badge>
                          </div>
                          <div className="mt-2 text-xs space-y-0.5">
                            <div className="flex justify-between"><span className="text-muted-foreground">Sales</span><strong>{fmtMoney(y.sale, cur)}</strong></div>
                            {terms.payout_model === "profit_share" && (
                              <div className="flex justify-between"><span className="text-muted-foreground">Expenses</span><strong>{fmtMoney(y.expenses, cur)}</strong></div>
                            )}
                            <div className="flex justify-between"><span className="text-muted-foreground">Owner Share</span><strong>{fmtMoney(y.owner_share, cur)}</strong></div>
                            <div className="flex justify-between"><span className="text-muted-foreground">MFPL Share</span><strong>{fmtMoney(y.mfpl_share, cur)}</strong></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Monthly Projection Table */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">
                    Monthly Projection ({cfg.projection_period_months} months)
                  </CardTitle>
                  <CardDescription>
                    Actual months are frozen. Future months use a flat required-base baseline —
                    seasonality is applied in Phase 2.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="overflow-auto max-h-[560px]">
                    <table className="w-full text-xs" data-testid="monthly-projection-table">
                      <thead className="bg-slate-100 sticky top-0 z-10">
                        <tr>
                          <th className="text-left p-2">Month</th>
                          <th className="text-center p-2">Type</th>
                          <th className="text-right p-2">Sale</th>
                          {terms.payout_model === "profit_share" && (
                            <>
                              <th className="text-right p-2">Expenses</th>
                              <th className="text-right p-2">Max Expense Ceiling</th>
                            </>
                          )}
                          <th className="text-right p-2">Share Base</th>
                          <th className="text-right p-2">Owner Share ({terms.owner_pct}%)</th>
                          {terms.payout_model === "profit_share" && (
                            <th className="text-right p-2">MFPL Share ({terms.company_pct}%)</th>
                          )}
                          <th className="text-right p-2">Cumulative Recovery</th>
                          <th className="text-right p-2">Recovery %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedMonths.map((r, idx) => (
                          <tr key={idx} className={r.source === "actual" ? "bg-emerald-50/50" : "bg-white"}>
                            <td className="p-2 font-medium">{r.month_label || r.month}</td>
                            <td className="p-2 text-center">
                              <Badge variant="outline" className={r.source === "actual" ? "text-emerald-700 border-emerald-300" : "text-blue-700 border-blue-300"}>
                                {r.source === "actual" ? "Actual" : "Projected"}
                              </Badge>
                            </td>
                            <td className="p-2 text-right">{fmtMoney(r.sale, cur)}</td>
                            {terms.payout_model === "profit_share" && (
                              <>
                                <td className="p-2 text-right">{fmtMoney(r.expenses, cur)}</td>
                                <td className="p-2 text-right text-amber-700">
                                  {r.source === "projected" ? fmtMoney(r.max_expense_ceiling, cur) : "—"}
                                </td>
                              </>
                            )}
                            <td className="p-2 text-right">{fmtMoney(r.share_base, cur)}</td>
                            <td className="p-2 text-right font-semibold text-emerald-700">{fmtMoney(r.owner_share, cur)}</td>
                            {terms.payout_model === "profit_share" && (
                              <td className="p-2 text-right text-violet-700">{fmtMoney(r.mfpl_share, cur)}</td>
                            )}
                            <td className="p-2 text-right">{fmtMoney(r.cumulative_owner, cur)}</td>
                            <td className="p-2 text-right">
                              <span className={r.recovery_pct >= (rec.ideal_pct_by_now || 0) - 5
                                ? "text-emerald-700 font-semibold"
                                : "text-amber-700"}>
                                {fmtPct(r.recovery_pct)}
                              </span>
                            </td>
                          </tr>
                        ))}
                        {paginatedMonths.length === 0 && (
                          <tr><td colSpan={9} className="p-4 text-center text-muted-foreground">
                            No projection rows available.
                          </td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Disclaimer */}
              <Alert className="mt-4 border-slate-300 bg-slate-50">
                <Info className="w-4 h-4 text-slate-600" />
                <AlertDescription className="text-slate-700 text-xs">
                  <strong>Disclaimer:</strong> {data.disclaimer}
                </AlertDescription>
              </Alert>
              </>}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );

  if (!strip) return body;
  return (
    <SummaryStrip Icon={Landmark} title="Investment Projection" desc="Franchise investment, return and recovery outlook." basis="Basis: since investment date (lifetime)"
      chips={metrics.length ? metrics.map((m) => <MetalChip key={m.k} label={m.label} value={m.value} sub={m.sub} tone={m.tone} testid={`or-inv-chip-${m.k}`} />)
        : <div className="col-span-full text-base text-[#5a3a22]" data-testid="or-inv-chip-empty">{loading ? "Loading projection…" : error || "No projection available."}</div>}
      open={detailsOpen} onToggle={() => setDetailsOpen(!detailsOpen)} detailsLabel="Investment Projection Details (Grid)" testid="or-inv-strip">
      {body}
    </SummaryStrip>
  );
}

export default InvestmentReturnProjection;
